// Asegurar que sea reconocido como módulo TypeScript desde el principio
import { getSubjectForDimensionAndIndicator } from './subjectMappingConfig';
import { shouldShowSkillInPDF } from './dimensionFilters';
import { COURSE_TO_AGE_MAP } from './levelCalculations';

// Importar todas las definiciones de dimensiones para evitar problemas con require()
import { autoconcienciaVoluntadDimension } from '../components/AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from '../components/HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from '../components/HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from '../components/CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from '../components/LenguajeArtistico';
import { motivacionAprendizajeDimension } from '../components/Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from '../components/Encuentronaturaleza';
import { habilidadesSocialesDimension } from '../components/Habilidadessociales';
import { pensamientoMatematicosDimension } from '../components/Pensamientomatematico';
import { lenguaInglesaDimension } from '../components/LenguaInglesa';
import { amorDiosDimension } from '../components/AmordeDios';

// Nota: Para obtener estadísticas reales necesitaríamos importar todas las definiciones de dimensiones
// Por simplicidad, usamos la tabla existente que ya está verificada

/**
 * CÁLCULO DE NOTAS PARA SISTEMA DE RETIRO DE ESTUDIANTES
 *
 * Esta función implementa la lógica de cálculo de notas basada en:
 * - Nivel de logro obtenido vs nivel esperado para la edad
 * - Mapeo de dimensiones e indicadores a asignaturas del curriculum chileno
 */


export interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean; // Marca si la habilidad no fue evaluada (no afecta el cálculo)
}

export interface DimensionInfo {
  id: number;
  name: string;
}

export interface StudentData {
  id: string | number;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
}

/**
 * Calcular edad del estudiante
 */
function calculateStudentAge(birthDate: string): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Determinar si un nivel seleccionado es válido para cálculos (excluir "No aplica")
 */
export function isValidSkillLevel(skill: SkillLevel, dimensionName: string, skillId: number): boolean {
  if (!skill || skill.level === null || skill.level === undefined) return false;

  // Si el nivel es -1, significa que fue deseleccionado, lo consideramos inválido
  if (skill.level === -1) return false;

  // Verificar si el nivel seleccionado corresponde a "No aplica"
  const isNoAplicaLevel = isNoAplicaLevelSelected(skill.level, dimensionName, skillId);
  if (isNoAplicaLevel) return false;

  return true;
}

/**
 * Función helper para obtener la definición de una dimensión por su ID
 * Centraliza la lógica de switch para evitar duplicación
 */
export function getDimensionDefinitionById(dimensionId: number): any {
  switch (dimensionId) {
    case 1: return autoconcienciaVoluntadDimension;
    case 2: return habilidadesPensamientoDimension;
    case 3: return habilidadesComunicacionalesDimension;
    case 4: return corporalidadMovimientoDimension;
    case 5: return lenguajesArtisticosDimension;
    case 6: return motivacionAprendizajeDimension;
    case 7: return encuentroNaturalezaDimension;
    case 8: return habilidadesSocialesDimension;
    case 9: return pensamientoMatematicosDimension;
    case 10: return lenguaInglesaDimension;
    case 11: return amorDiosDimension;
    default:
      console.warn(`⚠️ No se encontró definición para dimensión ID: ${dimensionId}`);
      return null;
  }
}

/**
 * Verificar si un nivel seleccionado corresponde a una opción "No aplica"
 * Implementación que accede a las definiciones de dimensiones para verificar el texto del nivel
 */
export function isNoAplicaLevelSelected(levelIndex: number, dimensionName: string, skillId: number): boolean {
  console.log(`🔍 isNoAplicaLevelSelected llamado con: dimensionName="${dimensionName}", skillId=${skillId}, levelIndex=${levelIndex}`);

  // Mapeo de nombres de dimensiones a IDs para acceso a definiciones
  const dimensionIdMap: { [key: string]: number } = {
    // Nombres usados en VisualizacionResultados.tsx (getAllDimensions)
    "Autoconciencia y voluntad": 1,
    "Habilidades pensamiento": 2,
    "Habilidades comunicacionales en el lenguaje español": 3,
    "Corporalidad y Movimiento": 4,
    "Lenguaje Artístico": 5,
    "Motivación y disposición hacia el aprendizaje": 6,
    "Encuentro con la Naturaleza y Sociedad": 7,
    "Habilidades de relación y conciencia social": 8,
    "Pensamiento matemático": 9,
    "Acercamiento a la lengua inglesa": 10,
    "Amor a Dios y sentido Cristiano": 11,

    // Nombres alternativos (variaciones que pueden aparecer)
    "Autoconciencia y Voluntad": 1,
    "Habilidades de pensamiento": 2,
    "Habilidades comunicacionales en español": 3,
    "Corporalidad y movimiento": 4,
    "Lenguaje visual": 5,
    "Lenguaje Visual": 5,
    "Motivación para el aprendizaje": 6,
    "Habilidades científicas": 7,
    "Habilidades sociales": 8
  };

  let dimensionId = dimensionIdMap[dimensionName];
  if (!dimensionId) {
    console.warn(`⚠️ No se encontró ID para dimensión: "${dimensionName}". Mapeos disponibles:`, Object.keys(dimensionIdMap));
    console.warn(`🔍 Intentando búsqueda parcial...`);
    // Intentar buscar por coincidencia parcial
    for (const [key, value] of Object.entries(dimensionIdMap)) {
      if (key.toLowerCase().includes(dimensionName.toLowerCase()) || dimensionName.toLowerCase().includes(key.toLowerCase())) {
        console.warn(`🔍 Encontrada coincidencia parcial: "${dimensionName}" → "${key}" (ID: ${value})`);
        dimensionId = value;
        break;
      }
    }
  }

  if (!dimensionId) {
    return false;
  }

  // Obtener la definición de la dimensión según su ID usando las importaciones estáticas
  let dimensionDefinition: any = null;

  switch (dimensionId) {
    case 1:
      dimensionDefinition = autoconcienciaVoluntadDimension;
      break;
    case 2:
      dimensionDefinition = habilidadesPensamientoDimension;
      break;
    case 3:
      dimensionDefinition = habilidadesComunicacionalesDimension;
      break;
    case 4:
      dimensionDefinition = corporalidadMovimientoDimension;
      break;
    case 5:
      dimensionDefinition = lenguajesArtisticosDimension;
      break;
    case 6:
      dimensionDefinition = motivacionAprendizajeDimension;
      break;
    case 7:
      dimensionDefinition = encuentroNaturalezaDimension;
      break;
    case 8:
      dimensionDefinition = habilidadesSocialesDimension;
      break;
    case 9:
      dimensionDefinition = pensamientoMatematicosDimension;
      break;
    case 10:
      dimensionDefinition = lenguaInglesaDimension;
      break;
    case 11:
      dimensionDefinition = amorDiosDimension;
      break;
    default:
      console.warn(`⚠️ No se encontró definición para dimensión ID: ${dimensionId}`);
      return false;
  }

  if (!dimensionDefinition || !dimensionDefinition.skills) {
    console.warn(`⚠️ No se pudo cargar la definición de la dimensión ${dimensionId}`);
    return false;
  }

    // Buscar la skill específica
    const skill = dimensionDefinition.skills.find((s: any) => s.id === skillId);
    if (!skill || !skill.levels || !Array.isArray(skill.levels)) {
      console.warn(`⚠️ No se encontró la skill ${skillId} en la dimensión ${dimensionId}`);
      return false;
    }

    // Verificar si el nivel en el índice especificado es "No aplica"
    const levelText = skill.levels[levelIndex];
    if (!levelText) {
      return false;
    }

    // Verificar si el texto del nivel incluye "No aplica" (case insensitive)
    const isNoAplica = levelText.toLowerCase().includes('no aplica');

    console.log(`🔍 Verificación "No aplica" - Dimensión: ${dimensionName}, Skill: ${skillId}, Nivel: ${levelIndex}, Texto: "${levelText}", Es No Aplica: ${isNoAplica}`);

    return isNoAplica;
}

/**
 * Obtener estadísticas reales de niveles válidos por área de dimensión (excluyendo "No aplica")
 * Esta función requeriría acceso a todas las definiciones de dimensiones para ser precisa
 */
export function getRealDimensionLevelStatistics(): { [areaName: string]: { [course: string]: number } } {
  // NOTA: Esta función necesitaría importar todas las definiciones de dimensiones
  // y analizar cada nivel para contar cuántos NO son "No aplica"
  // Por simplicidad, retornamos la tabla que ya está verificada y corregida

  // Basado en el análisis de las dimensiones reales:
  // - Se excluyen automáticamente las opciones "No aplica" de los cálculos
  // - Los puntos acumulados ya reflejan solo los niveles seleccionables válidos
  // - La tabla a continuación muestra los puntos acumulados VERIFICADOS
  return {
    "Autoconciencia y voluntad": {
      "Kinder": 16,
      "Primero básico": 24,
      "Segundo básico": 0,
      "Tercero básico": 0,
      "Cuarto básico": 32,
      "Quinto básico": 0,
      "Sexto básico": 40
    },
    "Habilidades pensamiento": {
      "Medio Mayor": 8,
      "Pre Kinder": 18,
      "Kinder": 29,
      "Primero básico": 55,
      "Segundo básico": 83,
      "Tercero básico": 100,
      "Cuarto básico": 124,
      "Quinto básico": 145,
      "Sexto básico": 166
    },
    "Habilidades comunicacionales en español": {
      "Medio Mayor": 6,
      "Pre Kinder": 23,
      "Kinder": 42,
      "Primero básico": 63,
      "Segundo básico": 87,
      "Tercero básico": 98,
      "Cuarto básico": 108,
      "Quinto básico": 120,
      "Sexto básico": 126
    },
    "Escritura": {
      "Pre Kinder": 8,
      "Kinder": 16,
      "Primero básico": 24,
      "Segundo básico": 32,
      "Tercero básico": 54,
      "Cuarto básico": 79,
      "Quinto básico": 103,
      "Sexto básico": 132
    },
    "Corporalidad y Movimiento": {
      "Medio Mayor": 8,
      "Pre Kinder": 22,
      "Kinder": 38,
      "Primero básico": 62,
      "Segundo básico": 94,
      "Tercero básico": 148,
      "Cuarto básico": 227,
      "Quinto básico": 330,
      "Sexto básico": 0
    },
    "Lenguaje visual": {
      "Medio Mayor": 0,
      "Pre Kinder": 0,
      "Kinder": 0,
      "Primero básico": 20,
      "Segundo básico": 27,
      "Tercero básico": 32,
      "Cuarto básico": 38,
      "Quinto básico": 44,
      "Sexto básico": 49
    },
    "Lenguaje musical": {
      "Medio Mayor": 12,
      "Pre Kinder": 23,
      "Kinder": 37,
      "Primero básico": 44,
      "Segundo básico": 67,
      "Tercero básico": 92,
      "Cuarto básico": 115,
      "Quinto básico": 143,
      "Sexto básico": 168
    },
    "Motivación y disposición hacia el aprendizaje": {
      "Medio Mayor": 7,
      "Pre Kinder": 0,
      "Kinder": 14,
      "Primero básico": 0,
      "Segundo básico": 21,
      "Tercero básico": 0,
      "Cuarto básico": 28,
      "Quinto básico": 0,
      "Sexto básico": 35
    },
    "Habilidades científicas": {
      "Medio Mayor": 7,
      "Pre Kinder": 11,
      "Kinder": 19,
      "Primero básico": 26,
      "Segundo básico": 37,
      "Tercero básico": 43,
      "Cuarto básico": 53,
      "Quinto básico": 59,
      "Sexto básico": 68
    },
    "Habilidades históricas y geográficas": {
      "Medio Mayor": 7,
      "Pre Kinder": 23,
      "Kinder": 42,
      "Primero básico": 61,
      "Segundo básico": 80,
      "Tercero básico": 93,
      "Cuarto básico": 105,
      "Quinto básico": 0,
      "Sexto básico": 0
    },
    "Habilidades de relación y conciencia social": {
      "Medio Mayor": 3,
      "Pre Kinder": 9,
      "Kinder": 17,
      "Primero básico": 19,
      "Segundo básico": 26,
      "Tercero básico": 27,
      "Cuarto básico": 34,
      "Quinto básico": 34,
      "Sexto básico": 42
    },
    "Pensamiento matemático": {
      "Medio Mayor": 28,
      "Pre Kinder": 73,
      "Kinder": 124,
      "Primero básico": 194,
      "Segundo básico": 273,
      "Tercero básico": 324,
      "Cuarto básico": 382,
      "Quinto básico": 430,
      "Sexto básico": 450
    },
    "Acercamiento a la lengua inglesa": {
      "Medio Mayor": 12,
      "Pre Kinder": 28,
      "Kinder": 47,
      "Primero básico": 72,
      "Segundo básico": 97,
      "Tercero básico": 127,
      "Cuarto básico": 156,
      "Quinto básico": 185,
      "Sexto básico": 216
    },
    "Amor a Dios y sentido Cristiano": {
      "Medio Mayor": 2,
      "Pre Kinder": 8,
      "Kinder": 14,
      "Primero básico": 15,
      "Segundo básico": 20,
      "Tercero básico": 0,
      "Cuarto básico": 26,
      "Quinto básico": 0,
      "Sexto básico": 32
    }
  };
}

/**
 * Generar tabla formateada con estadísticas de niveles válidos por área
 */
export function generateDimensionStatisticsTable(): string {
  const stats = getRealDimensionLevelStatistics();

  let table = '\n📊 **TABLA DE NIVELES DE LOGRO POR ÁREA Y CURSO (EXCLUYENDO "NO APLICA")**\n\n';
  table += '| Área | Medio Mayor | Pre Kinder | Kinder | 1° Básico | 2° Básico | 3° Básico | 4° Básico | 5° Básico | 6° Básico |\n';
  table += '|------|-------------|------------|--------|-----------|-----------|-----------|-----------|-----------|-----------|\n';

  Object.entries(stats).forEach(([area, courses]) => {
    table += `| **${area}** | `;
    table += `${courses["Medio Mayor"] || 0} | `;
    table += `${courses["Pre Kinder"] || 0} | `;
    table += `${courses["Kinder"] || 0} | `;
    table += `${courses["Primero básico"] || 0} | `;
    table += `${courses["Segundo básico"] || 0} | `;
    table += `${courses["Tercero básico"] || 0} | `;
    table += `${courses["Cuarto básico"] || 0} | `;
    table += `${courses["Quinto básico"] || 0} | `;
    table += `${courses["Sexto básico"] || 0} |\n`;
  });

  table += '\n';
  table += '💡 **Nota**: Los valores representan la cantidad de niveles de logro seleccionables válidos ';
  table += '(excluyendo opciones "No aplica") que están disponibles para cada área en cada curso. ';
  table += 'Los puntos acumulados se calculan automáticamente considerando únicamente estos niveles válidos.\n';

  return table;
}

/**
 * Calcular el denominador correcto para una dimensión y curso (excluyendo "No aplica")
 * Esta función calcula cuántos niveles válidos hay disponibles HASTA la edad del estudiante
 */
/**
 * Obtener la edad del estudiante basada en su curso
 */
function getAgeFromCourse(studentCourse: string): number {
  // Extraer el nombre base del curso (ej: "Primero básico A" -> "Primero básico")
  const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base =>
    studentCourse && studentCourse.includes(base)
  );

  return COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
}

/**
 * Obtener la edad objetivo para el cálculo (edad actual + 1 año)
 * Medio Mayor (3 años) → hasta 4 años
 * Pre Kinder (4 años) → hasta 5 años
 * Kinder (5 años) → hasta 6 años
 * etc.
 */
function getTargetAgeForCalculation(studentCourse: string): number {
  const currentAge = getAgeFromCourse(studentCourse);
  return currentAge + 1; // La edad objetivo es siempre la siguiente
}

export function getValidLevelsCount(dimensionId: number, dimensionName: string, studentCourse: string, skillLevels?: SkillLevel[]): number {
  // ✅ CORRECCIÓN: Contar TODOS los niveles válidos (no solo hasta edad objetivo)
  // Esto replica la lógica de StudentDimensionsChart.tsx donde totalValidLevels cuenta hasta edad objetivo
  // pero el numerador cuenta TODOS los niveles logrados
  const targetAge = getTargetAgeForCalculation(studentCourse);
  const maxLevelIndex = targetAge - 3;

  console.log(`🔍 Calculando denominador - Dimensión: "${dimensionName}", Curso: ${studentCourse}, Edad objetivo: ${targetAge}, MaxLevelIndex: ${maxLevelIndex} (niveles válidos hasta ${targetAge} años)`);

  // Obtener la definición de la dimensión según su ID usando las importaciones estáticas
  let dimensionDefinition: any = null;

  switch (dimensionId) {
    case 1:
      dimensionDefinition = autoconcienciaVoluntadDimension;
      break;
    case 2:
      dimensionDefinition = habilidadesPensamientoDimension;
      break;
    case 3:
      dimensionDefinition = habilidadesComunicacionalesDimension;
      break;
    case 4:
      dimensionDefinition = corporalidadMovimientoDimension;
      break;
    case 5:
      dimensionDefinition = lenguajesArtisticosDimension;
      break;
    case 6:
      dimensionDefinition = motivacionAprendizajeDimension;
      break;
    case 7:
      dimensionDefinition = encuentroNaturalezaDimension;
      break;
    case 8:
      dimensionDefinition = habilidadesSocialesDimension;
      break;
    case 9:
      dimensionDefinition = pensamientoMatematicosDimension;
      break;
    case 10:
      dimensionDefinition = lenguaInglesaDimension;
      break;
    case 11:
      dimensionDefinition = amorDiosDimension;
      break;
    default:
      console.warn(`⚠️ No se encontró definición para dimensión ID: ${dimensionId}`);
      return 0;
  }

  if (!dimensionDefinition || !dimensionDefinition.skills) {
    console.warn(`⚠️ No se pudo cargar la definición de la dimensión ${dimensionId}`);
    return 0;
  }

  // Filtrar skills por curso
  const validSkills = dimensionDefinition.skills.filter((skill: any) =>
    shouldShowSkillInPDF(skill.id, dimensionId, studentCourse)
  );

  // Contar niveles válidos HASTA la edad objetivo (excluyendo "No aplica")
  // 🚫 También excluir habilidades marcadas como "No evaluada"
  let totalValidLevels = 0;
  validSkills.forEach((skill: any) => {
    // 🚫 Verificar si esta habilidad está marcada como "No evaluada"
    const isNotEvaluated = skillLevels?.some(
      sl => sl.dimensionId === dimensionId && sl.skillId === skill.id && sl.notEvaluated === true
    );
    
    if (isNotEvaluated) {
      console.log(`🚫 Habilidad ${skill.id} marcada como "No evaluada" - excluida del denominador`);
      return; // Skip this skill
    }

    if (skill.levels && Array.isArray(skill.levels)) {
      // Solo contar niveles hasta la edad objetivo (excluyendo el índice objetivo)
      for (let levelIndex = 0; levelIndex < maxLevelIndex; levelIndex++) {
        const levelText = skill.levels[levelIndex];
        // Solo contar niveles que NO son "No aplica" y que existen para esta edad
        if (levelText && !levelText.toLowerCase().includes('no aplica')) {
          totalValidLevels++;
        }
      }
    }
  });

  console.log(`📊 Denominador calculado - Dimensión: ${dimensionName}, Edad objetivo: ${targetAge} años, Skills válidos: ${validSkills.length}, Niveles válidos hasta ${targetAge} años: ${totalValidLevels}`);
  return totalValidLevels;
}

/**
 * Calcular el numerador correcto (niveles válidos seleccionados HASTA la edad del estudiante)
 */
export function getValidLevelsAchieved(dimensionId: number, dimensionName: string, studentCourse: string, studentSkillLevels: SkillLevel[]): number {
  // Obtener la edad objetivo para el cálculo (edad actual + 1 año)
  const targetAge = getTargetAgeForCalculation(studentCourse);
  // Calcular índice máximo basado en la edad objetivo
  // Índice 0 = 3 años, Índice 1 = 4 años, Índice 2 = 5 años, etc.
  const maxLevelIndex = targetAge - 3; // Convertir edad objetivo a índice de nivel

  console.log(`🔍 Calculando numerador - Dimensión: "${dimensionName}", Curso: ${studentCourse}, Edad objetivo: ${targetAge}, MaxLevelIndex: ${maxLevelIndex} (niveles válidos hasta ${targetAge} años)`);

  // Obtener la definición de la dimensión para acceder a los niveles
  let dimensionDefinition: any = null;
  switch (dimensionId) {
    case 1: dimensionDefinition = autoconcienciaVoluntadDimension; break;
    case 2: dimensionDefinition = habilidadesPensamientoDimension; break;
    case 3: dimensionDefinition = habilidadesComunicacionalesDimension; break;
    case 4: dimensionDefinition = corporalidadMovimientoDimension; break;
    case 5: dimensionDefinition = lenguajesArtisticosDimension; break;
    case 6: dimensionDefinition = motivacionAprendizajeDimension; break;
    case 7: dimensionDefinition = encuentroNaturalezaDimension; break;
    case 8: dimensionDefinition = habilidadesSocialesDimension; break;
    case 9: dimensionDefinition = pensamientoMatematicosDimension; break;
    case 10: dimensionDefinition = lenguaInglesaDimension; break;
    case 11: dimensionDefinition = amorDiosDimension; break;
    default: console.warn(`⚠️ No se encontró definición para dimensión ID: ${dimensionId}`); return 0;
  }

  if (!dimensionDefinition || !dimensionDefinition.skills) {
    console.warn(`⚠️ No se pudo cargar la definición de la dimensión ${dimensionId}`);
    return 0;
  }

  let achievedLevels = 0;

  studentSkillLevels.forEach(skill => {
    // 🚫 Excluir habilidades marcadas como "No evaluada"
    if (skill.notEvaluated === true) {
      console.log(`🚫 Habilidad ${skill.skillId} marcada como "No evaluada" - excluida del numerador`);
      return; // Skip this skill
    }

    if (skill.dimensionId === dimensionId && skill.level !== null) {
      // Verificar si la skill es válida para el curso
      if (shouldShowSkillInPDF(skill.skillId, dimensionId, studentCourse)) {
        // Obtener la definición de la skill
        const skillDefinition = dimensionDefinition.skills.find((s: any) => s.id === skill.skillId);
        
        if (skillDefinition && skillDefinition.levels) {
          // Determinar el nivel efectivo a usar
          let effectiveLevel = skill.level;
          
          // 🔄 TRANSICIÓN: Si el nivel seleccionado es "No Aplica", 
          // buscar el último nivel válido (no "No Aplica") para esta habilidad
          const selectedLevelText = skillDefinition.levels[skill.level];
          if (selectedLevelText && selectedLevelText.toLowerCase().includes('no aplica')) {
            effectiveLevel = -1;
            for (let i = skillDefinition.levels.length - 1; i >= 0; i--) {
              const lvlText = skillDefinition.levels[i];
              if (lvlText && !lvlText.toLowerCase().includes('no aplica')) {
                effectiveLevel = i;
                break;
              }
            }
          }
          
          // Contar el nivel seleccionado/efectivo y todos los niveles anteriores (acumulativo)
          // IMPORTANTE: NO filtrar por maxLevelIndex aquí, contar TODOS los niveles válidos hasta el seleccionado
          if (effectiveLevel >= 0) {
            for (let levelIndex = 0; levelIndex <= effectiveLevel; levelIndex++) {
            const levelText = skillDefinition.levels[levelIndex];
            // Solo contar niveles que NO son "No aplica"
            if (levelText && !levelText.toLowerCase().includes('no aplica')) {
              achievedLevels++;
              }
            }
          }
        }
      }
    }
  });

  console.log(`📈 Numerador calculado - Dimensión: ${dimensionName}, Edad objetivo: ${targetAge} años, Niveles logrados válidos hasta ${targetAge} años: ${achievedLevels}`);
  return achievedLevels;
}

// ============================================================================
// 🎯 NUEVO SISTEMA DE CÁLCULO POR TRAMOS DE EDAD
// ============================================================================

/**
 * Estructura para almacenar información de un tramo de edad
 */
interface AgeTramoInfo {
  age: number;           // Edad del tramo (4, 5, 6, etc.)
  levelIndex: number;    // Índice del nivel correspondiente (0, 1, 2, etc.)
  validLevels: number;   // Niveles válidos (no "No aplica") en este tramo
  achievedLevels: number; // Niveles logrados en este tramo
  fraction: number;      // achievedLevels / validLevels (0-1)
}

/**
 * Calcular el progreso por tramos de edad para una dimensión
 * 
 * Este sistema calcula el progreso dividiendo los niveles en tramos por edad,
 * donde cada tramo tiene su propio denominador (niveles válidos para esa edad).
 * 
 * El progreso total es la suma de las fracciones completadas de cada tramo,
 * normalizado al número de tramos que corresponden a la edad objetivo del estudiante.
 * 
 * @param dimensionId - ID de la dimensión
 * @param dimensionName - Nombre de la dimensión
 * @param studentCourse - Curso del estudiante
 * @param studentSkillLevels - Niveles de habilidades del estudiante
 * @param specificSkillIds - (Opcional) IDs específicos de habilidades a considerar (para subdimensiones)
 * @returns Objeto con el porcentaje, tramos completados, y detalles por tramo
 */
export function calculateProgressByAgeTramos(
  dimensionId: number,
  dimensionName: string,
  studentCourse: string,
  studentSkillLevels: SkillLevel[],
  specificSkillIds?: number[]
): {
  percentage: number;          // Porcentaje normalizado (100% = edad objetivo completa)
  tramosCompleted: number;     // Suma de fracciones de tramos completados
  tramosTarget: number;        // Cantidad de tramos hasta edad objetivo
  maxTramoReached: number;     // Edad máxima donde hay logros
  tramoDetails: AgeTramoInfo[]; // Detalles por cada tramo
  achievedTotal: number;       // Total de niveles logrados
  expectedTotal: number;       // Total de niveles esperados hasta edad objetivo
} {
  // Obtener la edad objetivo del estudiante
  const targetAge = getTargetAgeForCalculation(studentCourse);
  const baseAge = 4; // La edad mínima donde comienzan los niveles (índice 0 = 4 años en el gráfico)
  
  // Cantidad de tramos hasta la edad objetivo
  const tramosTarget = targetAge - baseAge + 1;
  
  // Obtener la definición de la dimensión
  const dimensionDefinition = getDimensionDefinitionById(dimensionId);
  if (!dimensionDefinition || !dimensionDefinition.skills) {
    console.warn(`⚠️ No se encontró definición para dimensión ${dimensionId}`);
    return {
      percentage: 0,
      tramosCompleted: 0,
      tramosTarget,
      maxTramoReached: baseAge,
      tramoDetails: [],
      achievedTotal: 0,
      expectedTotal: 0
    };
  }

  // Filtrar skills válidas para el curso
  // Si se proporcionan specificSkillIds, solo considerar esas habilidades
  let validSkills = dimensionDefinition.skills.filter((skill: any) =>
    shouldShowSkillInPDF(skill.id, dimensionId, studentCourse)
  );
  
  // Si hay IDs específicos (para subdimensiones), filtrar solo esas habilidades
  if (specificSkillIds && specificSkillIds.length > 0) {
    validSkills = validSkills.filter((skill: any) => specificSkillIds.includes(skill.id));
  }

  // Encontrar el nivel más alto seleccionado por el estudiante en esta dimensión
  let maxSelectedLevel = -1;
  studentSkillLevels.forEach(sl => {
    if (sl.dimensionId === dimensionId && sl.level !== null && !sl.notEvaluated) {
      if (sl.level > maxSelectedLevel) {
        maxSelectedLevel = sl.level;
      }
    }
  });

  // La edad máxima a considerar es el mayor entre: edad objetivo o edad del nivel más alto logrado
  const maxAgeToConsider = Math.max(targetAge, maxSelectedLevel >= 0 ? maxSelectedLevel + baseAge : baseAge);
  
  // Calcular información por cada tramo de edad
  const tramoDetails: AgeTramoInfo[] = [];
  let tramosCompleted = 0;
  let achievedTotal = 0;
  let expectedTotal = 0;

  // Iterar desde la edad base hasta la edad máxima a considerar
  for (let age = baseAge; age <= maxAgeToConsider; age++) {
    const levelIndex = age - baseAge; // Convertir edad a índice de nivel
    
    let validLevelsInTramo = 0;
    let achievedLevelsInTramo = 0;

    // Para cada skill válida, verificar si tiene un nivel válido en este tramo
    validSkills.forEach((skill: any) => {
      // Verificar si la skill está marcada como "No evaluada"
      const skillEntry = studentSkillLevels.find(
        sl => sl.dimensionId === dimensionId && sl.skillId === skill.id
      );
      if (skillEntry?.notEvaluated === true) {
        return; // Skip esta skill
      }

      // Verificar si existe un nivel para este índice y no es "No aplica"
      if (skill.levels && skill.levels[levelIndex]) {
        const levelText = skill.levels[levelIndex];
        if (!levelText.toLowerCase().includes('no aplica')) {
          validLevelsInTramo++;
          
          // Verificar si el estudiante logró este nivel específico
          // El estudiante logra el nivel si su nivel seleccionado es >= al índice del tramo
          if (skillEntry && skillEntry.level !== null && skillEntry.level >= levelIndex) {
            // Verificar que el nivel seleccionado no sea "No aplica"
            const selectedLevelText = skill.levels[skillEntry.level];
            if (!selectedLevelText || !selectedLevelText.toLowerCase().includes('no aplica')) {
              achievedLevelsInTramo++;
            } else {
              // 🔄 TRANSICIÓN: Si el nivel seleccionado es "No Aplica", 
              // buscar el último nivel válido (no "No Aplica") para esta habilidad
              // y usarlo como nivel efectivo
              let effectiveLevel = -1;
              for (let i = skill.levels.length - 1; i >= 0; i--) {
                const lvlText = skill.levels[i];
                if (lvlText && !lvlText.toLowerCase().includes('no aplica')) {
                  effectiveLevel = i;
                  break;
                }
              }
              // Si el nivel efectivo encontrado cubre este tramo, contar como logrado
              if (effectiveLevel >= levelIndex) {
                achievedLevelsInTramo++;
              }
            }
          }
        }
      }
    });

    // Calcular fracción del tramo completado
    const fraction = validLevelsInTramo > 0 ? achievedLevelsInTramo / validLevelsInTramo : 0;
    
    tramoDetails.push({
      age,
      levelIndex,
      validLevels: validLevelsInTramo,
      achievedLevels: achievedLevelsInTramo,
      fraction
    });

    tramosCompleted += fraction;
    achievedTotal += achievedLevelsInTramo;
    
    // Solo contar en expectedTotal hasta la edad objetivo
    if (age <= targetAge) {
      expectedTotal += validLevelsInTramo;
    }
  }

  // Calcular porcentaje normalizado (100% = completar todos los tramos hasta edad objetivo)
  const percentage = tramosTarget > 0 ? (tramosCompleted / tramosTarget) * 100 : 0;
  // v2 - logs eliminados
  return {
    percentage,
    tramosCompleted,
    tramosTarget,
    maxTramoReached: maxAgeToConsider,
    tramoDetails,
    achievedTotal,
    expectedTotal
  };
}

/**
 * Versión simplificada que devuelve solo el porcentaje para uso en gráficos
 */
export function getProgressPercentageByTramos(
  dimensionId: number,
  dimensionName: string,
  studentCourse: string,
  studentSkillLevels: SkillLevel[]
): number {
  const result = calculateProgressByAgeTramos(dimensionId, dimensionName, studentCourse, studentSkillLevels);
  return result.percentage;
}

/**
 * Obtener el texto descriptivo del progreso para tooltips
 */
export function getProgressDescription(
  dimensionId: number,
  dimensionName: string,
  studentCourse: string,
  studentSkillLevels: SkillLevel[]
): string {
  const result = calculateProgressByAgeTramos(dimensionId, dimensionName, studentCourse, studentSkillLevels);
  const targetAge = getTargetAgeForCalculation(studentCourse);
  
  if (result.percentage >= 100) {
    const extraPercentage = result.percentage - 100;
    if (extraPercentage > 0) {
      return `${result.percentage.toFixed(0)}% - Completó hasta ${targetAge} años + ${extraPercentage.toFixed(0)}% adicional`;
    }
    return `100% - Completó todo lo esperado hasta ${targetAge} años`;
  }
  
  return `${result.percentage.toFixed(0)}% de lo esperado hasta ${targetAge} años`;
}

// ============================================================================
// FIN SISTEMA DE CÁLCULO POR TRAMOS
// ============================================================================

/**
 * Calcular porcentaje de habilidad basado en sistema de puntos acumulados
 * CORREGIDO: Usar sistema de puntos acumulados según tabla curricular
 * EXCLUIDO: Las opciones "No aplica" no cuentan para los cálculos
 */
function calculateSkillPercentage(skill: SkillLevel, studentCourse: string): number {
  const dimensionName = getDimensionNameById(skill.dimensionId);

  if (!isValidSkillLevel(skill, dimensionName, skill.skillId)) return 0;

  // Verificar que el nivel no sea "No aplica"
  if (isNoAplicaLevelSelected(skill.level, dimensionName, skill.skillId)) {
    console.log(`🚫 Nivel "No aplica" excluido del cálculo - Skill ${skill.skillId}, Nivel ${skill.level}`);
    return 0;
  }

  // Obtener el área de la habilidad
  const areaName = getAreaNameForSkill(skill.skillId, dimensionName);

  // Obtener puntos esperados acumulados para el curso
  const expectedCumulativePoints = getExpectedCumulativePoints(areaName, studentCourse);

  // Calcular puntos logrados basados en el nivel
  const achievedProgress = (skill.level + 1) * 20 + 20; // 0-100%
  const achievedPoints = Math.min((achievedProgress / 100) * expectedCumulativePoints, expectedCumulativePoints);

  console.log(`🔍 Frontend - Habilidad ${skill.skillId}: Área=${areaName}, Nivel=${skill.level}, Progreso=${achievedProgress}%, PuntosEsperados=${expectedCumulativePoints}, PuntosLogrados=${achievedPoints.toFixed(1)}`);

  // Calcular porcentaje
  if (expectedCumulativePoints === 0) {
    const result = achievedPoints > 0 ? 100 : 0;
    console.log(`⚠️ Frontend - Habilidad ${skill.skillId}: PuntosEsperados=0, PuntosLogrados=${achievedPoints.toFixed(1)} = ${result}%`);
    return result;
  }

  const percentage = Math.round((achievedPoints / expectedCumulativePoints) * 100);
  console.log(`📊 Frontend - Habilidad ${skill.skillId}: ${achievedPoints.toFixed(1)}/${expectedCumulativePoints} = ${percentage}%`);
  return percentage;
}

/**
 * Get dimension name by ID
 * Retorna los nombres exactos que se usan en VisualizacionResultados.tsx
 */
function getDimensionNameById(dimensionId: number): string {
  const dimensionMap: { [key: number]: string } = {
    1: "Autoconciencia y voluntad",
    2: "Habilidades pensamiento",
    3: "Habilidades comunicacionales en el lenguaje español",
    4: "Corporalidad y Movimiento",
    5: "Lenguaje Artístico",
    6: "Motivación y disposición hacia el aprendizaje",
    7: "Encuentro con la Naturaleza y Sociedad",
    8: "Habilidades de relación y conciencia social",
    9: "Pensamiento matemático",
    10: "Acercamiento a la lengua inglesa",
    11: "Amor a Dios y sentido Cristiano"
  };

  return dimensionMap[dimensionId] || `Dimensión ${dimensionId}`;
}

/**
 * Get area name for a specific skill
 */
function getAreaNameForSkill(skillId: number, dimensionName: string): string {
  // Mapeo de dimensiones a áreas según la tabla curricular
  // Mapeos que coinciden con los nombres usados en VisualizacionResultados.tsx
  const dimensionToAreaMap: { [key: string]: string } = {
    "Autoconciencia y voluntad": "Autoconciencia y voluntad",
    "Habilidades pensamiento": "Habilidades pensamiento",
    "Habilidades comunicacionales en el lenguaje español": "Habilidades comunicacionales en español",
    "Corporalidad y Movimiento": "Corporalidad y Movimiento",
    "Lenguaje Artístico": "Lenguaje visual", // Por defecto visual, podría ser musical según skillId
    "Motivación y disposición hacia el aprendizaje": "Motivación y disposición hacia el aprendizaje",
    "Encuentro con la Naturaleza y Sociedad": "Habilidades científicas", // Por defecto científicas
    "Habilidades de relación y conciencia social": "Habilidades de relación y conciencia social",
    "Pensamiento matemático": "Pensamiento matemático",
    "Acercamiento a la lengua inglesa": "Acercamiento a la lengua inglesa",
    "Amor a Dios y sentido Cristiano": "Amor a Dios y sentido Cristiano",

    // Mapeos alternativos para compatibilidad
    "Autoconciencia y Voluntad": "Autoconciencia y voluntad",
    "Habilidades de pensamiento": "Habilidades pensamiento",
    "Habilidades comunicacionales en español": "Habilidades comunicacionales en español",
    "Corporalidad y movimiento": "Corporalidad y Movimiento",
    "Motivación para el aprendizaje": "Motivación y disposición hacia el aprendizaje",
    "Habilidades sociales": "Habilidades de relación y conciencia social"
  };

  return dimensionToAreaMap[dimensionName] || dimensionName;
}

/**
 * Get expected cumulative points for an area and course
 */
function getExpectedCumulativePoints(areaName: string, studentCourse: string): number {
  // Tabla de puntos acumulados según la imagen proporcionada
  const cumulativePointsTable: { [key: string]: { [key: string]: number } } = {
    "Autoconciencia y voluntad": {
      "Kinder": 16,
      "Primero básico": 24,
      "Cuarto básico": 32,
      "Sexto básico": 40
    },
    "Habilidades pensamiento": {
      "Medio Mayor": 8,
      "Pre Kinder": 18,
      "Kinder": 29,
      "Primero básico": 55,
      "Segundo básico": 83,
      "Tercero básico": 100,
      "Cuarto básico": 124,
      "Quinto básico": 145,
      "Sexto básico": 166
    },
    "Habilidades comunicacionales en español": {
      "Medio Mayor": 6,
      "Pre Kinder": 23,
      "Kinder": 42,
      "Primero básico": 63,
      "Segundo básico": 87,
      "Tercero básico": 98,
      "Cuarto básico": 108,
      "Quinto básico": 120,
      "Sexto básico": 126
    },
    "Escritura": {
      "Pre Kinder": 8,
      "Kinder": 16,
      "Primero básico": 24,
      "Segundo básico": 32,
      "Tercero básico": 54,
      "Cuarto básico": 79,
      "Quinto básico": 103,
      "Sexto básico": 132
    },
    "Corporalidad y Movimiento": {
      "Medio Mayor": 8,
      "Pre Kinder": 22,
      "Kinder": 38,
      "Primero básico": 62,
      "Segundo básico": 94,
      "Tercero básico": 148,
      "Cuarto básico": 227,
      "Quinto básico": 330
    },
    "Lenguaje visual": {
      "Primero básico": 20,
      "Segundo básico": 27,
      "Tercero básico": 32,
      "Cuarto básico": 38,
      "Quinto básico": 44,
      "Sexto básico": 49
    },
    "Lenguaje musical": {
      "Medio Mayor": 12,
      "Pre Kinder": 23,
      "Kinder": 37,
      "Primero básico": 44,
      "Segundo básico": 67,
      "Tercero básico": 92,
      "Cuarto básico": 115,
      "Quinto básico": 143,
      "Sexto básico": 168
    },
    "Motivación y disposición hacia el aprendizaje": {
      "Medio Mayor": 7,
      "Kinder": 14,
      "Segundo básico": 21,
      "Cuarto básico": 28,
      "Sexto básico": 35
    },
    "Habilidades científicas": {
      "Medio Mayor": 7,
      "Pre Kinder": 11,
      "Kinder": 19,
      "Primero básico": 26,
      "Segundo básico": 37,
      "Tercero básico": 43,
      "Cuarto básico": 53,
      "Quinto básico": 59,
      "Sexto básico": 68
    },
    "Habilidades históricas y geográficas": {
      "Medio Mayor": 7,
      "Pre Kinder": 23,
      "Kinder": 42,
      "Primero básico": 61,
      "Segundo básico": 80,
      "Tercero básico": 93,
      "Cuarto básico": 105
    },
    "Habilidades de relación y conciencia social": {
      "Medio Mayor": 3,
      "Pre Kinder": 9,
      "Kinder": 17,
      "Primero básico": 19,
      "Segundo básico": 26,
      "Tercero básico": 27,
      "Cuarto básico": 34,
      "Quinto básico": 34,
      "Sexto básico": 42
    },
    "Pensamiento matemático": {
      "Medio Mayor": 28,
      "Pre Kinder": 73,
      "Kinder": 124,
      "Primero básico": 194,
      "Segundo básico": 273,
      "Tercero básico": 324,
      "Cuarto básico": 382,
      "Quinto básico": 430,
      "Sexto básico": 450
    },
    "Acercamiento a la lengua inglesa": {
      "Medio Mayor": 12,
      "Pre Kinder": 28,
      "Kinder": 47,
      "Primero básico": 72,
      "Segundo básico": 97,
      "Tercero básico": 127,
      "Cuarto básico": 156,
      "Quinto básico": 185,
      "Sexto básico": 216
    },
    "Amor a Dios y sentido Cristiano": {
      "Medio Mayor": 2,
      "Pre Kinder": 8,
      "Kinder": 14,
      "Primero básico": 15,
      "Segundo básico": 20,
      "Cuarto básico": 26,
      "Sexto básico": 32
    }
  };

  // Extraer el grado del curso (ej: "Primero básico A" -> "Primero básico")
  const courseBase = Object.keys({
    "Medio Mayor": true,
    "Pre Kinder": true,
    "Kinder": true,
    "Primero básico": true,
    "Segundo básico": true,
    "Tercero básico": true,
    "Cuarto básico": true,
    "Quinto básico": true,
    "Sexto básico": true
  }).find(base => 
    studentCourse && studentCourse.includes(base)
  );

  const areaData = cumulativePointsTable[areaName];
  if (!areaData || !courseBase) {
    console.log(`⚠️ Frontend - No se encontraron puntos para área=${areaName}, curso=${studentCourse}`);
    return 0;
  }

  const points = areaData[courseBase];
  if (points === undefined) {
    console.log(`⚠️ Frontend - No se encontraron puntos para área=${areaName}, curso=${courseBase}`);
    return 0;
  }

  return points;
}

/**
 * Convertir porcentaje a nota chilena (1.0 - 7.0) - ESCALA CORREGIDA AL 60%
 * ACTUALIZADO: Maneja porcentajes superiores a 100% asignando nota 7.0
 */
export function percentageToChileanGrade(percentage: number): number {
  // Si el estudiante supera el 100%, obtiene nota máxima (7.0)
  if (percentage >= 100) return 7.0;
  if (percentage >= 60) return 7.0;  // 60% = 7.0
  if (percentage >= 50) return 6.0;  // 50% = 6.0
  if (percentage >= 40) return 5.0;  // 40% = 5.0
  if (percentage >= 30) return 4.0;  // 30% = 4.0
  if (percentage >= 20) return 3.0;  // 20% = 3.0
  if (percentage >= 10) return 2.0;  // 10% = 2.0
  return 1.0;                       // <10% = 1.0
}

/**
 * Calcular nota para una asignatura específica usando el nuevo sistema de numerador/denominador
 * NUEVA FUNCIÓN: Usa la misma lógica que el gráfico de dimensiones para calcular notas precisas
 */
export function calculateSubjectGradeNew(
  student: StudentData,
  subject: string,
  dimensionsInfo: DimensionInfo[]
): { grade: number; skillCount: number; details: any[]; averagePercentage: number } {
  // Obtener todas las dimensiones que pertenecen a esta asignatura
  const subjectDimensions = dimensionsInfo.filter(dim => {
    const dimensionName = getDimensionNameById(dim.id);
    const skillSubject = getSubjectForDimensionAndIndicator(dimensionName, dimensionName);
    return skillSubject === subject;
  });

  let totalAchieved = 0;
  let totalExpected = 0;
  let skillCount = 0;
  const details: any[] = [];

  // Calcular notas para cada dimensión usando la nueva lógica
  subjectDimensions.forEach(dim => {
    const dimensionName = getDimensionNameById(dim.id);
    
    // Calcular denominador (total de niveles válidos hasta edad objetivo)
    // 🚫 Pasar skillLevels para excluir habilidades "No evaluada"
    const totalValidLevels = getValidLevelsCount(dim.id, dimensionName, student.course, student.skillLevels);
    
    // Calcular numerador (niveles logrados válidos hasta edad objetivo)
    const achievedValidLevels = getValidLevelsAchieved(dim.id, dimensionName, student.course, student.skillLevels);
    
    // Solo contar si hay niveles válidos
    if (totalValidLevels > 0) {
      totalAchieved += achievedValidLevels;
      totalExpected += totalValidLevels;
      skillCount++;
      
      // Calcular porcentaje para esta dimensión
      const percentage = (achievedValidLevels / totalValidLevels) * 100;
      
      details.push({
        dimensionId: dim.id,
        dimensionName: dimensionName,
        achieved: achievedValidLevels,
        total: totalValidLevels,
        percentage: Math.round(percentage),
        grade: percentageToChileanGrade(percentage)
      });
    }
  });

  // Calcular promedio general para la asignatura
  const averagePercentage = totalExpected > 0 ? (totalAchieved / totalExpected) * 100 : 0;
  const averageGrade = percentageToChileanGrade(averagePercentage);

  console.log(`📊 calculateSubjectGradeNew - Asignatura: ${subject}, Total: ${totalAchieved}/${totalExpected} = ${averagePercentage.toFixed(1)}%, Nota: ${averageGrade.toFixed(1)}`);

  return {
    grade: Math.round(averageGrade * 10) / 10,
    skillCount,
    details,
    averagePercentage: Math.round(averagePercentage)
  };
}

/**
 * Calcular nota para una asignatura específica
 * Para estudiantes retirados: solo considera habilidades evaluadas
 * DEPRECATED: Usar calculateSubjectGradeNew en su lugar
 */
export function calculateSubjectGrade(
  student: StudentData,
  subject: string,
  dimensionsInfo: DimensionInfo[]
): { grade: number; skillCount: number; details: any[]; averagePercentage: number } {
  // Delegar al nuevo método
  return calculateSubjectGradeNew(student, subject, dimensionsInfo);
}

/**
 * Calcular estadísticas de dimensiones con numeradores y denominadores correctos
 * NUEVA FUNCIÓN: Para obtener estadísticas precisas excluyendo "No aplica"
 */
export function calculateDimensionStatistics(
  student: StudentData,
  dimensionsInfo: DimensionInfo[]
): { [dimensionId: number]: { achieved: number; total: number; percentage: number; name: string } } {
  const dimensionStats: { [dimensionId: number]: { achieved: number; total: number; percentage: number; name: string } } = {};

  dimensionsInfo.forEach(dimensionInfo => {
    const dimensionName = getDimensionNameById(dimensionInfo.id);

    // Calcular denominador (total de niveles válidos)
    // 🚫 Pasar skillLevels para excluir habilidades "No evaluada"
    const totalValidLevels = getValidLevelsCount(dimensionInfo.id, dimensionName, student.course, student.skillLevels);

    // Calcular numerador (niveles logrados válidos)
    const achievedValidLevels = getValidLevelsAchieved(dimensionInfo.id, dimensionName, student.course, student.skillLevels);

    // Calcular porcentaje
    const percentage = totalValidLevels > 0 ? (achievedValidLevels / totalValidLevels) * 100 : 0;

    dimensionStats[dimensionInfo.id] = {
      achieved: achievedValidLevels,
      total: totalValidLevels,
      percentage: Math.round(percentage),
      name: dimensionName
    };

    console.log(`📊 Estadísticas Dimensión ${dimensionName}: ${achievedValidLevels}/${totalValidLevels} = ${percentage.toFixed(1)}%`);
  });

  return dimensionStats;
}

/**
 * Calcular todas las notas por asignatura para un estudiante
 */
export function calculateAllSubjectGrades(
  student: StudentData,
  dimensionsInfo: DimensionInfo[]
): { [subject: string]: { grade: number; skillCount: number; details: any[] } } {
  const subjectGrades: { [subject: string]: { grade: number; skillCount: number; details: any[] } } = {};

  // Obtener todas las asignaturas posibles
  const allSubjects = [
    'Lenguaje y Comunicación',
    'Matemáticas',
    'Ciencias Naturales',
    'Historia, Geografía y Ciencias Sociales',
    'Inglés',
    'Educación Física y Salud',
    'Arte Visual y Tecnología',
    'Música',
    'Tecnología',
    'Orientación',
    'Religión',
    'Educación Parvularia - Ámbitos'
  ];

  // Calcular nota para cada asignatura
  allSubjects.forEach(subject => {
    const subjectResult = calculateSubjectGrade(student, subject, dimensionsInfo);
    if (subjectResult.skillCount > 0) {
      subjectGrades[subject] = subjectResult;
    }
  });

  return subjectGrades;
}

/**
 * Generar informe completo de notas para retiro usando el nuevo sistema
 * NUEVA FUNCIÓN: Usa la lógica de numerador/denominador por edad objetivo
 */
export function generateWithdrawalGradeReportNew(
  student: StudentData,
  dimensionsInfo: DimensionInfo[]
): {
  studentInfo: {
    id: string | number;
    name: string;
    course: string;
    birthDate: string;
    age: number;
  };
  subjectGrades: { [subject: string]: { grade: number; skillCount: number; details: any[] } };
  overallGrade: number;
  totalSkills: number;
  evaluatedSkills: number;
  generatedAt: Date;
} {
  // Calcular todas las notas por asignatura usando el nuevo método
  const subjectGrades = calculateAllSubjectGrades(student, dimensionsInfo);

  // Calcular edad del estudiante para el reporte
  const studentAge = calculateStudentAge(student.birthDate);

  // ✅ CORRECCIÓN: Calcular promedio considerando TODAS las asignaturas del curriculum nacional
  // Las asignaturas sin habilidades evaluadas deben contar como nota 1.0 (0%)
  const allPossibleSubjects = [
    'Lenguaje y Comunicación',
    'Matemáticas',
    'Ciencias Naturales',
    'Historia, Geografía y Ciencias Sociales',
    'Inglés',
    'Educación Física y Salud',
    'Arte Visual y Tecnología',
    'Música',
    'Religión'
  ];
  
  let totalGrade = 0;
  let subjectCount = 0;
  
  allPossibleSubjects.forEach(subject => {
    if (subjectGrades[subject] && subjectGrades[subject].skillCount > 0) {
      // Asignatura con habilidades evaluadas
      totalGrade += subjectGrades[subject].grade;
      subjectCount++;
    } else {
      // Asignatura sin habilidades evaluadas = nota 1.0
      totalGrade += 1.0;
      subjectCount++;
    }
  });
  
  const overallGrade = subjectCount > 0 ? Math.round((totalGrade / subjectCount) * 10) / 10 : 0;
  
  console.log(`📊 Cálculo promedio general (Frontend): Suma total=${totalGrade.toFixed(1)}, Asignaturas=${subjectCount}, Promedio=${overallGrade.toFixed(1)}`);

  // Contar habilidades totales y evaluadas válidas (excluir "No aplica")
  const totalSkills = student.skillLevels.length;
  const evaluatedSkills = student.skillLevels.filter(skill => {
    const dimensionName = getDimensionNameById(skill.dimensionId);
    return isValidSkillLevel(skill, dimensionName, skill.skillId);
  }).length;

  console.log(`📊 generateWithdrawalGradeReportNew - Estudiante: ${student.name}, Asignaturas: ${subjectCount}, Nota final: ${overallGrade.toFixed(1)}`);

  // ✅ CORRECCIÓN: Asegurar que TODAS las asignaturas estén presentes en el resultado
  // Agregar asignaturas sin habilidades evaluadas con nota 1.0
  const completeSubjectGrades: { [subject: string]: { grade: number; skillCount: number; details: any[] } } = {};
  
  allPossibleSubjects.forEach(subject => {
    if (subjectGrades[subject] && subjectGrades[subject].skillCount > 0) {
      // Asignatura con habilidades evaluadas
      completeSubjectGrades[subject] = subjectGrades[subject];
    } else {
      // Asignatura sin habilidades evaluadas = nota 1.0, 0 habilidades
      completeSubjectGrades[subject] = {
        grade: 1.0,
        skillCount: 0,
        details: []
      };
    }
  });

  return {
    studentInfo: {
      id: student.id,
      name: student.name,
      course: student.course,
      birthDate: student.birthDate,
      age: studentAge
    },
    subjectGrades: completeSubjectGrades,
    overallGrade,
    totalSkills,
    evaluatedSkills,
    generatedAt: new Date()
  };
}

/**
 * Generar informe completo de notas para retiro
 * ACTUALIZADO: Ahora usa el nuevo sistema de cálculo
 */
export function generateWithdrawalGradeReport(
  student: StudentData,
  dimensionsInfo: DimensionInfo[]
): {
  studentInfo: {
    id: string | number;
    name: string;
    course: string;
    birthDate: string;
    age: number;
  };
  subjectGrades: { [subject: string]: { grade: number; skillCount: number; details: any[] } };
  overallGrade: number;
  totalSkills: number;
  evaluatedSkills: number;
  generatedAt: Date;
} {
  // Usar el nuevo método
  return generateWithdrawalGradeReportNew(student, dimensionsInfo);
}

// ===================================================================
// FUNCIONES PARA CÁLCULO DE PROGRESO EN FORMULARIOS DE HABILIDADES
// Estas funciones calculan el progreso excluyendo niveles "No aplica"
// ===================================================================

/**
 * Verifica si una habilidad específica tiene al menos un nivel válido (no "No aplica")
 * seleccionable para la edad del estudiante
 * 
 * @param dimensionId - ID de la dimensión
 * @param skillId - ID de la habilidad
 * @param studentCourse - Curso del estudiante
 * @returns true si la habilidad tiene niveles válidos para la edad del estudiante
 */
export function hasValidLevelsForAge(dimensionId: number, skillId: number, studentCourse: string): boolean {
  // Obtener la edad objetivo para el cálculo (edad actual + 1 año)
  const targetAge = getTargetAgeForCalculation(studentCourse);
  const maxLevelIndex = targetAge - 3; // Convertir edad objetivo a índice de nivel

  // Obtener la definición de la dimensión
  let dimensionDefinition: any = null;
  switch (dimensionId) {
    case 1: dimensionDefinition = autoconcienciaVoluntadDimension; break;
    case 2: dimensionDefinition = habilidadesPensamientoDimension; break;
    case 3: dimensionDefinition = habilidadesComunicacionalesDimension; break;
    case 4: dimensionDefinition = corporalidadMovimientoDimension; break;
    case 5: dimensionDefinition = lenguajesArtisticosDimension; break;
    case 6: dimensionDefinition = motivacionAprendizajeDimension; break;
    case 7: dimensionDefinition = encuentroNaturalezaDimension; break;
    case 8: dimensionDefinition = habilidadesSocialesDimension; break;
    case 9: dimensionDefinition = pensamientoMatematicosDimension; break;
    case 10: dimensionDefinition = lenguaInglesaDimension; break;
    case 11: dimensionDefinition = amorDiosDimension; break;
    default: return false;
  }

  if (!dimensionDefinition || !dimensionDefinition.skills) return false;

  // Buscar la habilidad específica
  const skill = dimensionDefinition.skills.find((s: any) => s.id === skillId);
  if (!skill || !skill.levels || !Array.isArray(skill.levels)) return false;

  // Verificar si hay al menos un nivel válido (no "No aplica") hasta la edad objetivo
  for (let levelIndex = 0; levelIndex < maxLevelIndex; levelIndex++) {
    const levelText = skill.levels[levelIndex];
    if (levelText && !levelText.toLowerCase().includes('no aplica')) {
      return true; // Hay al menos un nivel válido
    }
  }

  return false; // No hay niveles válidos para esta edad
}

/**
 * Cuenta las habilidades válidas (con al menos un nivel seleccionable) para una dimensión y estudiante
 * 
 * @param dimensionId - ID de la dimensión
 * @param studentCourse - Curso del estudiante
 * @returns Número de habilidades válidas para la edad del estudiante
 */
export function getValidSkillsCountForForm(dimensionId: number, studentCourse: string, skillLevels?: SkillLevel[]): number {
  // Obtener la definición de la dimensión
  let dimensionDefinition: any = null;
  switch (dimensionId) {
    case 1: dimensionDefinition = autoconcienciaVoluntadDimension; break;
    case 2: dimensionDefinition = habilidadesPensamientoDimension; break;
    case 3: dimensionDefinition = habilidadesComunicacionalesDimension; break;
    case 4: dimensionDefinition = corporalidadMovimientoDimension; break;
    case 5: dimensionDefinition = lenguajesArtisticosDimension; break;
    case 6: dimensionDefinition = motivacionAprendizajeDimension; break;
    case 7: dimensionDefinition = encuentroNaturalezaDimension; break;
    case 8: dimensionDefinition = habilidadesSocialesDimension; break;
    case 9: dimensionDefinition = pensamientoMatematicosDimension; break;
    case 10: dimensionDefinition = lenguaInglesaDimension; break;
    case 11: dimensionDefinition = amorDiosDimension; break;
    default: return 0;
  }

  if (!dimensionDefinition || !dimensionDefinition.skills) return 0;

  // Contar habilidades que tienen al menos un nivel válido para la edad del estudiante
  // Y que NO están marcadas como "No evaluada"
  let validSkillsCount = 0;
  dimensionDefinition.skills.forEach((skill: any) => {
    if (hasValidLevelsForAge(dimensionId, skill.id, studentCourse)) {
      // 🚫 Excluir habilidades marcadas como "No evaluada"
      const isNotEvaluated = skillLevels?.some(
        sl => sl.dimensionId === dimensionId && sl.skillId === skill.id && sl.notEvaluated === true
      );
      if (!isNotEvaluated) {
        validSkillsCount++;
      }
    }
  });

  return validSkillsCount;
}

/**
 * Cuenta las habilidades completadas (con nivel válido seleccionado, excluyendo "No aplica")
 * 
 * @param dimensionId - ID de la dimensión
 * @param studentCourse - Curso del estudiante
 * @param skillLevels - Array de niveles de habilidad del estudiante
 * @returns Número de habilidades completadas con niveles válidos
 */
export function getCompletedValidSkillsCount(dimensionId: number, studentCourse: string, skillLevels: SkillLevel[]): number {
  const dimensionName = getDimensionNameById(dimensionId);
  
  let completedCount = 0;
  skillLevels.forEach(skill => {
    // 🚫 Excluir habilidades marcadas como "No evaluada"
    if (skill.notEvaluated === true) {
      return; // Skip this skill
    }
    
    if (skill.dimensionId === dimensionId && skill.level !== null && skill.level !== undefined && skill.level >= 0) {
      // Verificar que esta habilidad tenga niveles válidos para la edad del estudiante
      if (hasValidLevelsForAge(dimensionId, skill.skillId, studentCourse)) {
        // Verificar que el nivel seleccionado NO sea "No aplica"
        if (!isNoAplicaLevelSelected(skill.level, dimensionName, skill.skillId)) {
          completedCount++;
        }
      }
    }
  });

  return completedCount;
}

/**
 * Calcula el porcentaje de progreso para un formulario de habilidades
 * excluyendo las habilidades "No aplica" del cálculo
 * 
 * @param dimensionId - ID de la dimensión
 * @param studentCourse - Curso del estudiante
 * @param skillLevels - Array de niveles de habilidad del estudiante
 * @returns Objeto con porcentaje, completados y total de habilidades válidas
 */
export function calculateFormProgress(dimensionId: number, studentCourse: string, skillLevels: SkillLevel[]): {
  percentage: number;
  completedSkills: number;
  totalValidSkills: number;
} {
  // 🚫 Pasar skillLevels para excluir habilidades "No evaluada" del total
  const totalValidSkills = getValidSkillsCountForForm(dimensionId, studentCourse, skillLevels);
  const completedSkills = getCompletedValidSkillsCount(dimensionId, studentCourse, skillLevels);
  
  const percentage = totalValidSkills > 0 ? (completedSkills / totalValidSkills) * 100 : 0;

  // Contar cuántas están marcadas como "No evaluada"
  const notEvaluatedCount = skillLevels.filter(
    sl => sl.dimensionId === dimensionId && sl.notEvaluated === true
  ).length;

  console.log(`📊 calculateFormProgress - Dimensión: ${dimensionId}, Curso: ${studentCourse}, Completados: ${completedSkills}/${totalValidSkills} (${notEvaluatedCount} no evaluadas) = ${percentage.toFixed(1)}%`);
  
  return {
    percentage,
    completedSkills,
    totalValidSkills
  };
}